// Client mirror of the next_fire_at math in the mirror-reminders-dispatch edge
// function. Used ONLY to seed next_fire_at when a reminder is created, so a new
// reminder becomes due at the right local moment. The dispatcher stays the
// source of truth at runtime (recompute + quiet-hours deferral); this does not
// apply quiet hours — the dispatcher handles that when it fires.
//
// Timezone-aware via Intl (browser-native), DST-tolerant for V1.

export type ScheduleType =
  | "times_per_day"
  | "every_n_hours"
  | "fixed_time"
  | "days_of_week";

export interface ScheduleConfig {
  times?: string[]; // times_per_day  → ["08:00","20:00"]
  hours?: number; // every_n_hours  → 3
  time?: string; // fixed_time / days_of_week → "09:00"
  days?: number[]; // days_of_week  → [1,3,5]  (0=Sun … 6=Sat)
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Parse "HH:MM" (or "HH:MM:SS") → minutes since local midnight. null if bad.
export function parseHHMM(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm) || h < 0 || h > 23 || mm < 0 || mm > 59) {
    return null;
  }
  return h * 60 + mm;
}

// Offset (local − UTC) in ms for the given instant in the given IANA tz.
function tzOffsetMs(timeZone: string, date: Date): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return asUtc - date.getTime();
}

// Convert a local wall-clock (Y/M/D + minutes-of-day) in tz → UTC ms.
function zonedToUtcMs(y: number, mo: number, d: number, minutes: number, timeZone: string): number {
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  const localAsUtc = Date.UTC(y, mo - 1, d, hh, mm, 0);
  let off = tzOffsetMs(timeZone, new Date(localAsUtc));
  let instant = localAsUtc - off;
  off = tzOffsetMs(timeZone, new Date(instant));
  instant = localAsUtc - off;
  return instant;
}

interface LocalParts {
  y: number;
  mo: number;
  d: number;
  dow: number;
}

const DOW_MAP: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

function localParts(ms: number, timeZone: string): LocalParts {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const map: Record<string, string> = {};
  for (const p of dtf.formatToParts(new Date(ms))) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  const dow = DOW_MAP[map.weekday];
  return {
    y: Number(map.year),
    mo: Number(map.month),
    d: Number(map.day),
    dow: dow === undefined ? 0 : dow,
  };
}

function nextLocalTimeUtcMs(fromMs: number, timeZone: string, targetMin: number): number {
  for (let addDays = 0; addDays <= 2; addDays++) {
    const probe = localParts(fromMs + addDays * DAY_MS, timeZone);
    const candidate = zonedToUtcMs(probe.y, probe.mo, probe.d, targetMin, timeZone);
    if (candidate > fromMs) return candidate;
  }
  return fromMs + DAY_MS;
}

// Next due UTC ms for a schedule, strictly after fromMs. null if config is
// malformed. Quiet hours are NOT applied here (the dispatcher does that).
export function computeNextFireUtcMs(
  scheduleType: ScheduleType,
  cfg: ScheduleConfig,
  fromMs: number,
  timeZone: string,
): number | null {
  if (scheduleType === "every_n_hours") {
    const hours = Number(cfg.hours);
    if (!Number.isFinite(hours) || hours <= 0) return null;
    return fromMs + Math.round(hours * 60 * 60 * 1000);
  }

  if (scheduleType === "fixed_time") {
    const t = parseHHMM(cfg.time);
    if (t === null) return null;
    return nextLocalTimeUtcMs(fromMs, timeZone, t);
  }

  if (scheduleType === "times_per_day") {
    const raw = Array.isArray(cfg.times) ? cfg.times : [];
    let best = Number.POSITIVE_INFINITY;
    for (const item of raw) {
      const tm = parseHHMM(item);
      if (tm === null) continue;
      const c = nextLocalTimeUtcMs(fromMs, timeZone, tm);
      if (c < best) best = c;
    }
    return Number.isFinite(best) ? best : null;
  }

  if (scheduleType === "days_of_week") {
    const t = parseHHMM(cfg.time);
    if (t === null) return null;
    const days = Array.isArray(cfg.days)
      ? cfg.days.filter((n) => Number.isInteger(n) && n >= 0 && n <= 6)
      : [];
    if (days.length === 0) return null;
    for (let addDays = 0; addDays <= 8; addDays++) {
      const probe = localParts(fromMs + addDays * DAY_MS, timeZone);
      if (days.indexOf(probe.dow) === -1) continue;
      const candidate = zonedToUtcMs(probe.y, probe.mo, probe.d, t, timeZone);
      if (candidate > fromMs) return candidate;
    }
    return null;
  }

  return null;
}

// Resolve the device IANA timezone as a sensible fallback when the user has no
// whisper-preferences row yet.
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo";
  } catch {
    return "America/Sao_Paulo";
  }
}
